import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  OnChangeFn,
  SortingState,
  useReactTable,
  VisibilityState,
} from '@tanstack/react-table'
import * as Apollo from '@apollo/client'
import { ReactNode, useCallback, useMemo, useState } from 'react'
import { ListViewToolbar } from './ListViewToolbar'
import {
  Alert,
  AlertTitle,
  Box,
  Checkbox,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material'
import { HeaderTableCell } from '../Table/HeaderTableCell'
import { TableItemCell } from '../Table/TableItemCell'
import { ListViewPagination } from './ListViewPagination'
import { FormCode, DocumentType } from '@kistill/shared/types'
import { visuallyHidden } from '@mui/utils'
import { handleFilter } from './filterHandlers'
import { getFieldsForType, FilterParams, getAllFieldKeys } from './filterConfig'
import { SelectItem } from '../../types/SelectItem'
import { useNavigate } from 'react-router-dom'
import { useSelectedGuids } from '../../contexts/SelectedGuidsContext'
import { useListViewStorage } from '../../contexts/ListViewStorageContext'
import { useListViewSelection } from '../../hooks/useListViewSelection'
import { useListViewPagination, DEFAULT_PAGE_SIZE } from '../../hooks/useListViewPagination'
import { useListViewSorting } from '../../hooks/useListViewSorting'
import { useListViewDocument } from '../../hooks/useListViewDocument'
import { useListViewDataUpdate } from '../../hooks/useListViewDataUpdate'
import { useSortedItemsGuidsLazyQuery } from '@kistill/client/data-access'
import { useSnackbar } from 'notistack'

interface ColumnVisibility {
  [key: string]: boolean
}

interface ListViewProps<T> {
  columns: ColumnDef<T>[]
  query: Apollo.DocumentNode
  title?: string
  filter?: ReactNode
  queryName: string
  columnDefaultVisibility?: ColumnVisibility
  type?: FormCode | string | undefined
  showFilter?: boolean
  sortBy: string
  hasCheckbox?: boolean
  onRowDoubleClick?: (row: T) => void
  setCheckedRows?: (connectedItems: string[]) => void
  urlRoute?: string
  dataUpdateTrigger?: boolean
  setDataUpdateTrigger?: (value: boolean) => void
  updateListViewData?: () => void
  checkedRows?: string[] | undefined
}

export const ListView = <T extends { guid: string }>({
  columns,
  query,
  title,
  queryName,
  columnDefaultVisibility,
  type,
  showFilter,
  sortBy,
  hasCheckbox,
  onRowDoubleClick,
  setCheckedRows,
  urlRoute,
  dataUpdateTrigger,
  setDataUpdateTrigger,
  updateListViewData,
  checkedRows,
}: ListViewProps<T>) => {
  const [sorting, setSorting] = useState<SortingState>([])
  const {
    columnVisibility,
    setColumnVisibility: _setColumnVisibility,
    initialFilterParams,
    saveFilterParams,
  } = useListViewStorage(type, columnDefaultVisibility)
  const [isLoading, setIsLoading] = useState(false)
  const navigate = useNavigate()
  const { setSelectedGuids, setSource } = useSelectedGuids()

  const initialFilterForQuery = useMemo(() => {
    if (!type || !initialFilterParams) return undefined
    const filterObj = handleFilter(type, initialFilterParams)
    return Object.keys(filterObj).length > 0 ? filterObj : undefined
  }, [type, initialFilterParams])

  const { data, loading, error, refetch } = Apollo.useQuery(query, {
    variables: {
      type: type,
      pagination: { pageIndex: 0, pageSize: DEFAULT_PAGE_SIZE },
      ...(initialFilterForQuery && { filter: initialFilterForQuery }),
    },
    fetchPolicy: 'network-only',
  })

  const [fetchItemsByGuids] = Apollo.useLazyQuery(query, {
    fetchPolicy: 'network-only',
  })

  // Calculate values early for hooks
  const totalGuids = (data && data[queryName]?.totalGuids) ?? []
  const totalResults =
    (data && data[queryName]?.list?.pageInfo?.totalResults) ?? 0

  // Initialize pagination hook
  const { pageIndex, pageSize, pagination, setPagination, updatePagination } =
    useListViewPagination({
      onPaginationChange: async ({
        pageIndex: newPageIndex,
        pageSize: newPageSize,
      }) => {
        setIsLoading(true)
        const refetchVariables = {
          type: type,
          pagination: { pageIndex: newPageIndex, pageSize: newPageSize },
          ...(initialFilterForQuery && { filter: initialFilterForQuery }),
        }
        await refetch(
          refetchVariables as {
            type?: string
            pagination?: { pageIndex: number; pageSize: number }
            filter?: Record<string, unknown>
          },
        )
        setIsLoading(false)
      },
      dataLength: totalResults,
    })

  // Initialize sorting hook
  const { sortConfig, setSortConfig, createSortHandler } = useListViewSorting({
    defaultSortBy: sortBy,
    onSortChange: async (sort, sortBy) => {
      const refetchVariables = {
        sort,
        sortBy,
        type: type,
        ...(initialFilterForQuery && { filter: initialFilterForQuery }),
      }
      await refetch(
        refetchVariables as {
          type?: string
          sort?: string
          sortBy?: string
          filter?: Record<string, unknown>
        },
      )
    },
    setIsLoading,
  })

  // Initialize document hook
  const { handleOpenDocument: handleOpenDocumentInternal } =
    useListViewDocument({
      title,
      urlRoute,
    })

  const [getSortedItemsGuids] = useSortedItemsGuidsLazyQuery()
  const { enqueueSnackbar } = useSnackbar()
  const isItemList = type === 'items' || (type && Object.values(FormCode).includes(type as FormCode))

  const memoizedData = useMemo(() => data, [data])
  const memoizedColumns = useMemo(() => columns, [columns])

  // Initialize selection hook early
  const {
    selected,
    setSelectedAllPages,
    handleClick,
    handleSelectAllClick,
    checkboxStateIndeterminate,
    checkboxStateChecked,
    isSelected,
  } = useListViewSelection<T>({
    checkedRows,
    setCheckedRows,
    pageIndex,
    dataLength: totalResults,
    pageSize,
  })

  // Initialize data update hook
  useListViewDataUpdate({
    dataUpdateTrigger,
    setDataUpdateTrigger,
    onUpdate: async () => {
      const refetchVariables = {
        type: type,
        pagination: { pageIndex, pageSize },
        ...(initialFilterForQuery && { filter: initialFilterForQuery }),
      }
      await refetch(
        refetchVariables as {
          type?: string
          pagination?: { pageIndex: number; pageSize: number }
          filter?: Record<string, unknown>
        },
      )
    },
    pageIndex,
    pageSize,
    setIsLoading,
  })

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const search = (query?: string | null) => {
    const newIndex = 0
    setPagination({ pageIndex: newIndex, pageSize })
    // To remove default sort when there's a search input
    if (query) {
      setSortConfig({ key: '', direction: 'asc' })
    }

    const refetchVariables = {
      pagination: { pageIndex: newIndex, pageSize },
      type: type,
      ...(initialFilterForQuery && { filter: initialFilterForQuery }),
    }

    if (query !== null && query !== undefined) {
      const searchVariables = {
        search: { query },
      }
      setIsLoading(true)

      refetch({ ...refetchVariables, ...searchVariables }).then(() =>
        setIsLoading(false),
      )
    } else {
      refetch(refetchVariables)
    }
  }

  const filter = (
    showSelected?: string,
    selectedGuids?: [] | null,
    // All other fields - order matches getAllFieldKeys() from filterConfig
    ...restArgs: (
      | SelectItem[]
      | string
      | number
      | string[]
      | number[]
      | null
      | undefined
    )[]
  ) => {
    const newIndex = 0

    // Build params object dynamically from config
    const fields = getFieldsForType(type)
    const params: FilterParams = {} as FilterParams
    if (showSelected) {
      params.selectedGuids = selected as [] | null
    } else {
      params.selectedGuids = null
    }

    // Get field keys in order from config (excluding showSelected and selectedGuids)
    const fieldKeys = getAllFieldKeys()

    // Create a map of all argument values by field name
    // restArgs[0] corresponds to fieldKeys[0], etc.
    const allArgs: Record<
      string,
      SelectItem[] | string | number | string[] | number[] | null | undefined
    > = {}
    for (let i = 0; i < fieldKeys.length; i++) {
      if (i < restArgs.length) {
        allArgs[fieldKeys[i]] = restArgs[i]
      }
    }

    // Extract only the fields that are relevant for this type
    for (const fieldKey of fields) {
      if (allArgs[fieldKey] !== undefined) {
        params[fieldKey as keyof FilterParams] = allArgs[
          fieldKey
        ] as FilterParams[keyof FilterParams]
      }
    }

    const refetchVariables: Partial<{
      pagination: { pageIndex: number; pageSize: number }
      type: string | undefined
      filter: Record<string, unknown>
      search: null
    }> = {
      pagination: { pageIndex: newIndex, pageSize },
      type: type,
    }

    const filterObj = handleFilter(type ? type : '', params)

    if (filterObj) {
      setIsLoading(true)
      refetchVariables.filter = filterObj
    } else {
      refetchVariables.search = null
    }

    saveFilterParams(params)

    refetch(
      refetchVariables as {
        type?: string
        filter?: Record<string, unknown>
        search?: null
      },
    ).then(() => setIsLoading(false))
  }

  const sortedData: T[] = useMemo(() => {
    if (memoizedData && memoizedData[queryName]?.list?.items) {
      const sortedItems = memoizedData[queryName]?.list?.items
      return sortedItems
    }
    return []
  }, [memoizedData, queryName])

  const handleColumnVisibilityChange: OnChangeFn<VisibilityState> = (
    updaterOrValue,
  ) => {
    // Handle both functional updates and direct value updates
    _setColumnVisibility((prev) => {
      const newValue =
        typeof updaterOrValue === 'function'
          ? updaterOrValue(prev || {}) // Call the updater function with the previous state
          : updaterOrValue // Use the new value directly
      return newValue
    })
  }

  const table = useReactTable({
    data: sortedData || [],
    columns: memoizedColumns,
    pageCount:
      Math.ceil(
        ((data && data[queryName]?.list?.pageInfo.totalResults) ?? 0) /
          pageSize,
      ) || 1,
    state: {
      sorting,
      pagination,
      columnVisibility, // Dynamically control column visibility
    },
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    onColumnVisibilityChange: handleColumnVisibilityChange, // Use the adapted function
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualSorting: true,
    manualPagination: true,
    enableHiding: true, // Allow columns to be hidden
    debugTable: false,
  })

  const _rows: T[] = table.getRowModel().rows.map((row) => row.original)

  const handleOpenItemFiltering = () => {
    if (type === 'itemCollection' || type === 'origin') {
      setSelectedGuids([...selected])
      setSource(type)
    }
    navigate(`/eintak`)
  }

  const refetchList = useCallback(async () => {
    const refetchVariables = {
      type: type,
      pagination: { pageIndex, pageSize },
      ...(initialFilterForQuery && { filter: initialFilterForQuery }),
    }
    await refetch(
      refetchVariables as {
        type?: string
        pagination?: { pageIndex: number; pageSize: number }
        filter?: Record<string, unknown>
      },
    )
  }, [refetch, type, initialFilterForQuery, pageIndex, pageSize])

  const onOpenChangeLocation =
    type === 'items'
      ? async (selectedIds: string[]): Promise<boolean> => {
          const { data: result, error } = await fetchItemsByGuids({
            variables: {
              type: 'items',
              filter: { selectedGuids: selectedIds },
              pagination: {
                pageIndex: 0,
                pageSize: Math.max(selectedIds.length, 1),
              },
            },
          })
          if (error) {
            enqueueSnackbar('Villa kom upp við að sækja eintök.', {
              variant: 'error',
            })
            return false
          }
          const items = (result?.[queryName]?.list?.items ?? []) as {
            formCode?: string
          }[]
          const allowed = ['KVIKS', 'GOGNGR', 'GOGN', 'SAFNMUN']
          const allAllowed =
            items.length === selectedIds.length &&
            items.every(
              (item) => item.formCode && allowed.includes(item.formCode),
            )
          if (!allAllowed) {
            enqueueSnackbar(
              'Eingöngu er leyfilegt að breyta staðsetningum fyrir eintök af tegundunum kvikmyndir stafrænar, gögn, gögn stafræn og safnmunir.',
              { variant: 'error' },
            )
            return false
          }
          return true
        }
      : undefined

  return (
    <>
      <ListViewToolbar
        onSearch={search}
        onFilter={filter}
        showFilter={showFilter}
        type={type}
        selected={selected}
        setSelected={setSelectedAllPages}
        initialFilterParams={initialFilterParams ?? undefined}
        onOpenChangeLocation={onOpenChangeLocation}
        refetchList={refetchList}
        handleOpenDocument={async (enumType: DocumentType) => {
          let guids = selected.length > 0 ? selected : totalGuids
          if (selected.length > 0 && isItemList) {
            try {
              const { data: d } = await getSortedItemsGuids({
                variables: {
                  selectedGuids: [...selected],
                  sort: sortConfig.direction || 'asc',
                  sortBy: sortConfig.key || sortBy,
                },
              })
              if (d?.sortedItemsGuids?.length) guids = d.sortedItemsGuids
            } catch {
              enqueueSnackbar('Ekki tókst að sækja skjal', { variant: 'error' })
              return
            }
          }
          await handleOpenDocumentInternal(enumType, guids, totalGuids)
        }}
        totalGuids={totalGuids}
        updateListViewData={updateListViewData}
        sortConfig={sortConfig}
        handleOpenItemFiltering={handleOpenItemFiltering}
        columns={columns
          .filter((col) => {
            const colWithAccessor = col as ColumnDef<T> & {
              accessorKey?: string
            }
            const id = col.id || colWithAccessor.accessorKey
            const header = col.header as string
            // Never show guid-related columns, edit column, or columns with empty headers
            const isGuidColumn = id && id.toLowerCase().includes('guid')
            const isEditColumn = id === 'edit'
            const hasEmptyHeader = !header || header === ''
            return id && !isGuidColumn && !isEditColumn && !hasEmptyHeader
          })
          .map((col) => {
            const colWithAccessor = col as ColumnDef<T> & {
              accessorKey?: string
            }
            return {
              id: (col.id || colWithAccessor.accessorKey || '') as string,
              header: col.header as string,
            }
          })}
        columnVisibility={columnVisibility || {}}
        setColumnVisibility={_setColumnVisibility}
      />
      {(loading || isLoading) && !error && <LinearProgress />}
      <TableContainer
        sx={{
          maxHeight: 'calc(100vh - 280px)',
          overflow: 'auto',
        }}
      >
        {error ? (
          <Alert severity='error' sx={{ m: 2 }}>
            <AlertTitle>Villa</AlertTitle>
            Ekki tókst að sækja gögn
          </Alert>
        ) : (
        <Table
          sx={{
            minWidth: 650,
            '& .MuiTableCell-stickyHeader': {
              backgroundColor: (theme) =>
                theme.palette.mode === 'dark'
                  ? theme.palette.background.paper
                  : theme.palette.grey[200],
            },
          }}
          size='medium'
          stickyHeader
        >
          <TableHead>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {hasCheckbox ||
                  (hasCheckbox === undefined && (
                    <HeaderTableCell padding='checkbox'>
                      <Checkbox
                        color='primary'
                        indeterminate={checkboxStateIndeterminate(
                          table.getRowModel().rows,
                        )}
                        checked={checkboxStateChecked(table.getRowModel().rows)}
                        onChange={() =>
                          handleSelectAllClick(table.getRowModel().rows)
                        }
                        inputProps={{
                          'aria-label': 'select all',
                        }}
                      />
                    </HeaderTableCell>
                  ))}
                {headerGroup.headers.map((header) => (
                  <HeaderTableCell
                    key={header.id}
                    align={'left'}
                    sortDirection={
                      sortConfig.key === header.id &&
                      (sortConfig.direction === 'asc' ||
                        sortConfig.direction === 'desc')
                        ? sortConfig.direction
                        : undefined
                    }
                    colSpan={header.colSpan}
                  >
                    {header.column.columnDef.enableSorting === true ? (
                      <TableSortLabel
                        active={sortConfig.key === header.id}
                        hideSortIcon={true}
                        direction={
                          sortConfig.key === header.id &&
                          (sortConfig.direction === 'desc' ||
                            sortConfig.direction === 'asc')
                            ? sortConfig.direction === 'desc'
                              ? 'asc'
                              : 'desc'
                            : 'asc'
                        }
                        onClick={createSortHandler<T>(header)}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                        {sortConfig.key === header.id ? (
                          <Box component='span' sx={visuallyHidden}>
                            {sortConfig.direction === 'desc'
                              ? 'sorted descending'
                              : 'sorted ascending'}
                          </Box>
                        ) : null}
                      </TableSortLabel>
                    ) : (
                      flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )
                    )}
                  </HeaderTableCell>
                ))}
              </TableRow>
            ))}
          </TableHead>
          <TableBody>
            {!loading && !isLoading && table.getRowModel().rows.length === 0 ? (
              <TableRow>
                <TableItemCell colSpan={99} align='center' sx={{ py: 4 }}>
                  Engin gögn fundust
                </TableItemCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((row, index) => {
              const isItemSelected = isSelected(row)
              return (
                <TableRow
                  hover
                  key={row.id}
                  onClick={(event) =>
                    handleClick(event, row, index, table.getRowModel().rows)
                  }
                  onDoubleClick={() =>
                    onRowDoubleClick ? onRowDoubleClick(row.original) : null
                  }
                  role='checkbox'
                  tabIndex={-1}
                  selected={
                    hasCheckbox || (hasCheckbox === undefined && isItemSelected)
                  }
                  sx={{
                    '&:last-child td, &:last-child th': { border: 'none' },
                  }}
                >
                  {hasCheckbox ||
                    (hasCheckbox === undefined && (
                      <TableItemCell padding='checkbox'>
                        <Checkbox
                          color='primary'
                          checked={isItemSelected}
                          sx={{ pl: 1.5 }}
                        />
                      </TableItemCell>
                    ))}
                  {row.getVisibleCells().map((cell) => (
                    <TableItemCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableItemCell>
                  ))}
                </TableRow>
              )
            })
            )}
          </TableBody>
        </Table>
        )}
      </TableContainer>
      <Stack
        sx={{
          ...(type !== 'connectedItems' && type !== 'itemLocations'
            ? { backgroundColor: (theme) => theme.palette.background.default }
            : { backgroundColor: (theme) => theme.palette.background.paper }),
          borderTop: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
        <ListViewPagination
          count={totalResults ?? 0}
          pageIndex={pageIndex}
          rowsPerPage={pageSize}
          update={updatePagination}
        />
      </Stack>
    </>
  )
}
